import { OrderSizeType as GeneratedOrderSizeType } from '@moment/horizon-data-access/proposal'

export enum OrderSide {
  Buy = 'buy',
  Sell = 'sell',
  Both = 'both',
  Any = 'any',
}

// Short names over the generated union, which stays the single definition of
// the wire values: a hand-written enum would drift from the spec.
export const OrderSizeType = {
  Par: GeneratedOrderSizeType.OrderSizeTypePar,
  Notional: GeneratedOrderSizeType.OrderSizeTypeNotional,
  Shares: GeneratedOrderSizeType.OrderSizeTypeShares,
} as const

export type OrderSizeType = GeneratedOrderSizeType

export enum OrderStatus {
  PendingApproval = 'pending_approval',
  // Behind a BE feature flag. When enabled, the BE emits `pending_review`
  // directly instead of `pending_approval` + a FlaggedForReview event. The FE
  // maps it to TraderViewStatus.ApprovalRequired (see compute-order-status.ts).
  PendingReview = 'pending_review',
  PendingExecution = 'pending_execution',
  PartiallyFilled = 'partially_filled',
  Filled = 'filled',
  Canceled = 'canceled',
  Rejected = 'rejected',
}

export enum OrderType {
  Limit = 'limit',
  Market = 'market',
  RFQResponse = 'rfq_response', // Will be used when we start sending RFQs first and then create orders from the responses
  // Advisor-initiated quote wanted (Bid Wanted on a sell, Offer Wanted on a
  // buy): no price at entry; it becomes a held parent order with an open
  // inquiry that a trader later prices. Wire value matches the
  // horizon/orders-api `quote_wanted` order type.
  QuoteWanted = 'quote_wanted',
}

export enum OrderStructureType {
  Single = 'single',
  Block = 'block',
}

export enum ClientOrderCreationSource {
  AccountDetailsTicket = 'account_details_ticket',
  AccountHoldingsTicket = 'account_holdings_ticket',
  AdvisorBulkOrderUpload = 'advisor_bulk_order_upload',
  // Placeholder — no dedicated advisor order entry ticket exists yet, but
  // the advisor blotter's SidePane needs a provider source to avoid a
  // runtime crash when OrderEntry calls useOrderSource().
  AdvisorOrderEntryTicket = 'advisor_order_entry_ticket',
  AllocationOptimization = 'allocation_optimization',
  BondDetailsTicket = 'bond_details_ticket',
  ProposalGeneration = 'proposal_generation',
  // Placeholder — the /proposals page has no dedicated order-entry surface,
  // but its SidePane can still mount OrderEntry when openPane state leaks
  // across client-side navigation, so it needs a provider source to avoid a
  // runtime crash when OrderEntry calls useOrderSource().
  ProposalsTicket = 'proposals_ticket',
  StreamlinedOrderTicket = 'streamlined_order_ticket',
  TradeDeskBulkUpload = 'trade_desk_bulk_upload',
  TradeDeskBulkUploadEditChecks = 'trade_desk_bulk_upload_edit_checks',
  TradeDeskBulkUploadSkipValidation = 'trade_desk_bulk_upload_skip_validation',
  TradeDeskBulkUploadLplStreetTrades = 'trade_desk_bulk_upload_lpl_street_trades',
  TradeDeskOrderEntryTicket = 'trade_desk_order_entry_ticket',
  SellAll = 'sell_all',
  ClientListBulkOrders = 'client_list_bulk_orders',
  OfferingsIoiTicket = 'offerings_ioi_ticket',
}

export enum OrderMetadataFields {
  TraderId = 'trader_id',
  HasDiscretionaryAuthority = 'has_discretionary_authority',
  IsSolicited = 'is_solicited',
  /** The advisor the order belongs to, as opposed to whoever entered it. */
  AdvisorUserId = 'advisor_user_id',
  AdvisorName = 'advisor_name',
  AdvisorEmail = 'advisor_email',
  /** The advisor's free-text note for the desk reviewing the order. */
  Annotations = 'annotations',
  Subfirm = 'subfirm',
  Platform = 'platform',
  SubmissionName = 'submission_name',
  OverrideBuyingPower = 'override_buying_power',
  OverridePrimeBrokerage = 'override_prime_brokerage',
  OverrideCustodianUnknownForUnknownAccounts = 'override_custodian_unknown_for_unknown_accounts',
  OverrideAccountTradingGroupMismatch = 'override_account_trading_group_mismatch',
  Source = 'source',
  SubmitterId = 'submitter_id',
  QuoteSnapshot = 'quote_snapshot',
  MomentSleeveId = 'moment_sleeve_id',
}

export const OrderBookingType = {
  Cash: 'cash',
  Margin: 'margin',
} as const

export type OrderBookingTypeType =
  (typeof OrderBookingType)[keyof typeof OrderBookingType]
